/**
 * EdCity School ID Mapping — "Import Mapping Excel".
 *
 * Pure logic for the file half of the import: the downloadable .xlsx template,
 * the parser that turns an uploaded workbook into rows the server can
 * validate, and the plan reader. (The CSV parser is kept for tests and for
 * any future text upload; the dialog accepts .xlsx only.) Kept out of AdminTaskDialog so it can be
 * unit-tested with plain jest (see jest.config.js).
 *
 * WHY THE TEMPLATE IS AN .xlsx. An SCRN is a 12-digit number. Excel shows a
 * 12-digit General-format number as "1E+11" and, when saving as CSV, writes
 * that DISPLAY into the file — the low digits are gone before we ever see
 * the file. Two things fix that at the source: the template's SCRN column is
 * pre-formatted as Text (so a typed code stays "100000000008"), and an
 * uploaded .xlsx is read from the cell VALUES, which keep all 12 digits even
 * when the cell is numeric. The dialog therefore takes .xlsx ONLY.
 *
 * One row = one school, and EVERY column is required: SCRN, the school name
 * as EdCity states it, our School_id, and the plan column (體驗版/基本版/優越版)
 * holding ONE of those three names — the school gets that plan's
 * subscription, cloned from the template bound to it on the 訂閱綁定
 * (Subscription Binding) page. A row missing any of them is refused by line. The older three-flag layout (one column per
 * plan, marked with 1/Y/✓) is still read for files made from the first template.
 */

import * as XLSX from 'xlsx';

// Plan keys as the server knows them. Order = column order in the template.
export const EDCITY_IMPORT_PLAN_KEYS = ['trial', 'basic', 'premium'];

export const EDCITY_IMPORT_PLAN_LABELS = {
  trial: '體驗版',
  basic: '基本版',
  premium: '優越版',
};

export const EDCITY_IMPORT_PLAN_LABELS_EN = {
  trial: 'Trial',
  basic: 'Basic',
  premium: 'Premium',
};

// The template's header row, in order. The parser accepts aliases (below), so
// a file edited in Excel with a slightly different header still loads.
export const EDCITY_IMPORT_PLAN_COLUMN = '體驗版/基本版/優越版';

export const EDCITY_IMPORT_CSV_HEADERS = [
  'SCRN',
  'School Name (EdCity)',
  'School_id',
  EDCITY_IMPORT_PLAN_COLUMN,
];

// Added to the workbook the admin downloads after a successful import.
export const EDCITY_IMPORT_ADMIN_USERNAME_COLUMN = 'School Admin Username';
export const EDCITY_IMPORT_ADMIN_PASSWORD_COLUMN = 'School Admin Password';

export const EDCITY_IMPORT_RESULT_HEADERS = [
  ...EDCITY_IMPORT_CSV_HEADERS,
  EDCITY_IMPORT_ADMIN_USERNAME_COLUMN,
  EDCITY_IMPORT_ADMIN_PASSWORD_COLUMN,
];

export const EDCITY_IMPORT_MAX_ROWS = 300;

export function buildEdcityImportCsvTemplate() {
  // UTF-8 BOM so Excel opens the Chinese headers correctly.
  return '\uFEFF' + EDCITY_IMPORT_CSV_HEADERS.join(',') + '\r\n';
}

export function edcityImportCsvTemplateFileName() {
  return 'edcity_school_mapping_template.csv';
}

export function edcityImportTemplateFileName() {
  return 'edcity_school_mapping_template.xlsx';
}

export function edcityImportResultFileName() {
  return 'edcity_school_mapping_with_admins.xlsx';
}

// The plan cell of the downloadable mapping file: the names that were on the
// row, in template order. One plan stays a single label; a legacy multi-plan
// row joins them.
export function edcityImportPlanCell(plans) {
  const labels = [];
  (plans || []).forEach((p) => {
    if (!p) return;
    const label = p.label || EDCITY_IMPORT_PLAN_LABELS[p.plan] || '';
    if (label) labels.push(label);
  });
  return labels.join('、');
}

/**
 * The uploaded mapping, plus School Admin Username / School Admin Password.
 * SCRN, School_id and both admin columns are text cells so Excel does not
 * rewrite a 12-digit SCRN or a password. `rows` are the commit result rows
 * (each may carry `school_admin`). Returns an ArrayBuffer.
 */
export function buildEdcityImportResultWorkbook(rows) {
  const body = (rows || []).map((row) => {
    const admin = (row && row.school_admin) || {};
    // A collided username is not this school's admin. Leave both cells blank
    // rather than hand out a login that does not belong to the school.
    const showAccount = !!(admin.created || admin.existed);
    const schoolId = row && row.school_id != null && row.school_id !== '' ? String(row.school_id) : '';
    return [
      row && row.scrn ? String(row.scrn) : '',
      row && row.school_name_edcity ? String(row.school_name_edcity) : '',
      schoolId,
      edcityImportPlanCell(row && row.plans),
      showAccount && admin.username ? String(admin.username) : '',
      showAccount && admin.password ? String(admin.password) : '',
    ];
  });
  const ws = XLSX.utils.aoa_to_sheet([EDCITY_IMPORT_RESULT_HEADERS, ...body]);
  const textCols = [0, 2, 4, 5];
  for (let r = 1; r <= body.length; r++) {
    textCols.forEach((c) => {
      const addr = XLSX.utils.encode_cell({ c, r });
      if (!ws[addr]) return;
      ws[addr].t = 's';
      ws[addr].v = String(ws[addr].v == null ? '' : ws[addr].v);
      ws[addr].z = '@';
    });
  }
  ws['!cols'] = [{ wch: 16 }, { wch: 44 }, { wch: 12 }, { wch: 24 }, { wch: 26 }, { wch: 24 }];
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, 'Mapping');
  return XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
}

/**
 * The Excel template: header row, and the SCRN column pre-filled with EMPTY
 * TEXT cells (number format "@") for every row the import accepts. SheetJS
 * community edition cannot write a column-level style, so the cells exist
 * up front — a code typed into one of them stays text and keeps its digits.
 * Returns an ArrayBuffer ready to hand to a Blob.
 */
export function buildEdcityImportTemplateWorkbook() {
  const ws = XLSX.utils.aoa_to_sheet([EDCITY_IMPORT_CSV_HEADERS]);
  for (let r = 2; r <= EDCITY_IMPORT_MAX_ROWS + 1; r++) {
    ws['A' + r] = { t: 's', v: '', z: '@' };
  }
  ws['!ref'] = 'A1:' + XLSX.utils.encode_cell({ c: EDCITY_IMPORT_CSV_HEADERS.length - 1, r: EDCITY_IMPORT_MAX_ROWS });
  ws['!cols'] = [{ wch: 16 }, { wch: 44 }, { wch: 12 }, { wch: 24 }];
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, 'Mapping');
  return XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
}

// A worksheet cell VALUE as the text the parser expects. Numbers are
// stringified from the value, never from Excel's display format — a General
// cell holding 100000000008 DISPLAYS "1E+11" but its value has every digit.
export function cellText(value) {
  if (value == null) return '';
  if (typeof value === 'number') {
    return Number.isFinite(value) ? String(value) : '';
  }
  if (typeof value === 'boolean') return value ? 'TRUE' : 'FALSE';
  if (value instanceof Date) return value.toISOString().slice(0, 10);
  return String(value);
}

export function isSpreadsheetFileName(name) {
  return /\.(xlsx|xlsm|xls)$/i.test(String(name || ''));
}

// Excel with some regional settings exports semicolon- or tab-separated files.
export function sniffCsvDelimiter(text) {
  const firstLine = String(text || '').split(/\r\n|\r|\n/, 1)[0] || '';
  let best = ',';
  let bestCount = 0;
  [',', ';', '\t'].forEach((d) => {
    const count = firstLine.split(d).length - 1;
    if (count > bestCount) {
      best = d;
      bestCount = count;
    }
  });
  return best;
}

// Minimal CSV parser: quoted fields, escaped quotes ("") and CR/LF/CRLF ends.
export function parseCsvText(text, delimiter) {
  const rows = [];
  let row = [];
  let field = '';
  let inQuotes = false;
  for (let i = 0; i < text.length; i++) {
    const c = text[i];
    if (inQuotes) {
      if (c === '"') {
        if (text[i + 1] === '"') {
          field += '"';
          i++;
        } else {
          inQuotes = false;
        }
      } else {
        field += c;
      }
    } else if (c === '"') {
      inQuotes = true;
    } else if (c === delimiter) {
      row.push(field);
      field = '';
    } else if (c === '\n' || c === '\r') {
      row.push(field);
      rows.push(row);
      row = [];
      field = '';
      if (c === '\r' && text[i + 1] === '\n') {
        i++;
      }
    } else {
      field += c;
    }
  }
  if (field !== '' || row.length > 0) {
    row.push(field);
    rows.push(row);
  }
  return rows;
}

// Header cell → canonical column key, or null for a column we ignore.
export function normalizeEdcityImportHeader(name) {
  const raw = String(name || '').replace(/^\uFEFF/, '').trim();
  const key = raw.toLowerCase().replace(/[\s\-()（）]+/g, '_').replace(/^_+|_+$/g, '');
  const aliases = {
    scrn: 'scrn',
    school_code: 'scrn',
    edb_scrn: 'scrn',
    school_name_edcity: 'school_name_edcity',
    school_name_edcity_: 'school_name_edcity',
    edcity_school_name: 'school_name_edcity',
    edcity_name: 'school_name_edcity',
    edcity_school: 'school_name_edcity',
    note: 'school_name_edcity',
    school_name: 'school_name_edcity',
    school_id: 'school_id',
    schoolid: 'school_id',
    school: 'school_id',
    id: 'school_id',
    trial: 'trial',
    basic: 'basic',
    premium: 'premium',
    plan: 'plan',
    plan_type: 'plan',
    subscription: 'plan',
    subscription_type: 'plan',
    tier: 'plan',
  };
  if (aliases[key]) {
    return aliases[key];
  }
  // Chinese plan headers are matched on the raw text, since the lowercase /
  // underscore normalisation above is a no-op on CJK.
  const compact = raw.replace(/\s+/g, '');
  if (compact === EDCITY_IMPORT_PLAN_COLUMN || compact === '訂閱類型' || compact === '訂閱') return 'plan';
  if (compact === '體驗版' || compact === '体验版') return 'trial';
  if (compact === '基本版') return 'basic';
  if (compact === '優越版' || compact === '优越版') return 'premium';
  if (compact === 'EdCity學校名稱') return 'school_name_edcity';
  if (compact === '學校ID' || compact === '學校編號') return 'school_id';
  return null;
}

// A plan cell is "marked" for any of the usual yes-ish spellings. Blank, 0,
// N/no/否 and anything unrecognised are NOT marked, so a stray value never
// silently grants a plan.
export function parsePlanFlag(value) {
  const v = String(value == null ? '' : value).trim().toLowerCase();
  if (v === '') return false;
  return ['1', 'y', 'yes', 'true', 'x', '✓', '✔', '是', 'v', 'o', '○', '●'].includes(v);
}

// Excel writes a 12-digit General-format number back to CSV as it DISPLAYED
// it — "1E+11" or "1.90101E+11" — and the low digits are gone from the file.
// Expanding "1E+11" would silently import SCRN 100000000000 for a school whose
// real code is 100000000008, so such a cell is refused, never repaired. The
// fix is on the Excel side (format the SCRN column as Text).
export function isExcelExponent(value) {
  return /^\d+(\.\d+)?e[+-]?\d+$/i.test(String(value == null ? '' : value).trim());
}

// The plan column's cell → plan key, or null when blank, or undefined when the
// text is not a plan name (the row is then refused, never silently unplanned).
export function parsePlanValue(value) {
  const raw = String(value == null ? '' : value).replace(/\s+/g, '');
  if (raw === '') return null;
  const v = raw.toLowerCase();
  if (v === '體驗版' || v === '体验版' || v === 'trial') return 'trial';
  if (v === '基本版' || v === 'basic') return 'basic';
  if (v === '優越版' || v === '优越版' || v === 'premium') return 'premium';
  return undefined;
}

/**
 * Parse an uploaded CSV into import rows.
 *
 * Returns { rows, error }. `error` is a Chinese message for the admin when the
 * file cannot be used at all (empty, no header, missing required column, too
 * many rows); per-row problems are NOT decided here — the server's dry run
 * reports those, because most of them need the database (school lookup,
 * existing mapping, bound templates). The one exception is an unrecognised
 * plan name, which only this parser sees: the row is returned with
 * `plan_invalid` set (and no plan) so the dialog can refuse it by line.
 * Likewise an SCRN Excel mangled into scientific notation is returned with
 * `scrn_exponent: true` so the dialog can say WHY the code is wrong.
 */
export function parseEdcityImportCsv(rawText) {
  const text = String(rawText || '').replace(/^\uFEFF/, '');
  if (text.trim() === '') {
    return { rows: [], error: '檔案是空的' };
  }
  return parseEdcityImportTable(parseCsvText(text, sniffCsvDelimiter(text)));
}

/**
 * Parse an uploaded .xlsx/.xls (ArrayBuffer) — first worksheet — into import
 * rows. Same result shape as parseEdcityImportCsv.
 */
export function parseEdcityImportWorkbook(arrayBuffer) {
  let wb;
  try {
    wb = XLSX.read(arrayBuffer, { type: 'array' });
  } catch (e) {
    return { rows: [], error: '無法讀取 Excel 檔案' };
  }
  const sheetName = wb.SheetNames && wb.SheetNames[0];
  const ws = sheetName ? wb.Sheets[sheetName] : null;
  if (!ws) {
    return { rows: [], error: '檔案是空的' };
  }
  const table = XLSX.utils.sheet_to_json(ws, { header: 1, raw: true, defval: '' })
    .map((cells) => (cells || []).map(cellText));
  return parseEdcityImportTable(table);
}

/**
 * Shared tail of both parsers: a 2-D array of cell strings (row 1 = header).
 */
export function parseEdcityImportTable(table) {
  const entries = (table || [])
    .map((cells, i) => ({ cells: (cells || []).map((c) => String(c == null ? '' : c)), line: i + 1 }))
    .filter((e) => e.cells.some((c) => c.trim() !== ''));
  if (entries.length === 0) {
    return { rows: [], error: '檔案是空的' };
  }
  if (entries.length < 2) {
    return { rows: [], error: '檔案沒有資料行 (第一行必須是欄位標題，第二行開始才是學校資料)' };
  }
  const columnMap = {};
  entries[0].cells.forEach((h, idx) => {
    const canonical = normalizeEdcityImportHeader(h);
    if (canonical && columnMap[canonical] === undefined) {
      columnMap[canonical] = idx;
    }
  });
  const hasPlanColumn = columnMap.plan !== undefined;
  const hasLegacyPlanColumns = ['trial', 'basic', 'premium'].every((h) => columnMap[h] !== undefined);
  const required = ['scrn', 'school_name_edcity', 'school_id'];
  const missing = required.filter((h) => columnMap[h] === undefined);
  if (!hasPlanColumn && !hasLegacyPlanColumns) {
    missing.push('plan');
  }
  if (missing.length > 0) {
    const labels = { scrn: 'SCRN', school_name_edcity: 'School Name (EdCity)', school_id: 'School_id', plan: EDCITY_IMPORT_PLAN_COLUMN };
    return { rows: [], error: '檔案缺少必要欄位: ' + missing.map((m) => labels[m]).join(', ') };
  }
  const dataEntries = entries.slice(1);
  if (dataEntries.length > EDCITY_IMPORT_MAX_ROWS) {
    return { rows: [], error: `一次最多只能匯入 ${EDCITY_IMPORT_MAX_ROWS} 間學校，請分批上傳` };
  }
  const pick = (cells, key) => {
    const idx = columnMap[key];
    return (idx === undefined || idx >= cells.length) ? '' : String(cells[idx]).trim();
  };
  const rows = dataEntries.map((e) => {
    const row = {
      line: e.line,
      scrn: pick(e.cells, 'scrn'),
      school_name_edcity: pick(e.cells, 'school_name_edcity'),
      school_id: pick(e.cells, 'school_id'),
      plans: { trial: false, basic: false, premium: false },
    };
    if (isExcelExponent(row.scrn)) {
      row.scrn_exponent = true;
    }
    if (hasPlanColumn) {
      const text = pick(e.cells, 'plan');
      const plan = parsePlanValue(text);
      if (plan === undefined) {
        row.plan_invalid = text;
      } else if (plan) {
        row.plans[plan] = true;
      }
    } else {
      row.plans = {
        trial: parsePlanFlag(pick(e.cells, 'trial')),
        basic: parsePlanFlag(pick(e.cells, 'basic')),
        premium: parsePlanFlag(pick(e.cells, 'premium')),
      };
    }
    // Every column is required. Blank cells are decided HERE (the server
    // would only see an empty string) so the dialog can refuse them by line.
    const missingCells = [];
    if (row.scrn === '') missingCells.push('scrn');
    if (row.school_name_edcity === '') missingCells.push('school_name_edcity');
    if (row.school_id === '') missingCells.push('school_id');
    if (!markedPlans(row).length && row.plan_invalid === undefined) missingCells.push('plan');
    if (missingCells.length) {
      row.missing = missingCells;
    }
    return row;
  });
  return { rows, error: null };
}

// Plan keys marked on a row, in template order.
export function markedPlans(row) {
  const plans = (row && row.plans) || {};
  return EDCITY_IMPORT_PLAN_KEYS.filter((k) => !!plans[k]);
}
